import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Divider,
  TextField,
  Button,
  IconButton,
  Badge,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Comment as CommentIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { magicMarkerService } from '../magicMarkerService';

interface CollaborativeViewProps {
  session: any;
  documentId: string;
  onClose: () => void;
}

interface Participant {
  user_id: string;
  user_name: string;
  active: boolean;
  joined_at: string;
  cursor_position?: { x: number; y: number };
}

interface CollaborationEvent {
  id: string;
  user_name: string;
  action: string;
  timestamp: string;
  details: any;
}

const CollaborativeView: React.FC<CollaborativeViewProps> = ({
  session,
  documentId,
  onClose,
}) => {
  const [participants, setParticipants] = useState<Participant[]>(session.participants || []);
  const [history, setHistory] = useState<CollaborationEvent[]>([]);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState<'participants' | 'activity'>('participants');

  useEffect(() => {
    loadCollaborationHistory();
    // In a real app, this would set up WebSocket connections for real-time updates
  }, [documentId]);

  const loadCollaborationHistory = async () => {
    try {
      const response = await magicMarkerService.getCollaborationHistory(documentId);
      setHistory(response.events || []);
    } catch (error) {
      console.error('Failed to load collaboration history:', error);
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'add_annotation':
        return <AddIcon fontSize="small" />;
      case 'edit_annotation':
        return <EditIcon fontSize="small" />;
      case 'reply_to_annotation':
        return <CommentIcon fontSize="small" />;
      case 'resolve_annotation':
        return <CheckIcon fontSize="small" />;
      default:
        return <CommentIcon fontSize="small" />;
    }
  };

  const getActionColor = (action: string): any => {
    switch (action) {
      case 'add_annotation':
        return 'primary';
      case 'edit_annotation':
        return 'secondary';
      case 'resolve_annotation':
        return 'success';
      default:
        return 'grey';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <Drawer
      anchor="right"
      open={true}
      onClose={onClose}
      sx={{
        width: 400,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 400,
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Collaboration</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Session Info */}
        <Box sx={{ mb: 2, p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Session ID: {session.session_id}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Chip
              label={`${participants.filter(p => p.active).length} Active`}
              size="small"
              color="success"
              icon={<PersonIcon />}
            />
            <Chip
              label={session.can_edit ? 'Can Edit' : 'View Only'}
              size="small"
              color={session.can_edit ? 'primary' : 'default'}
            />
          </Box>
        </Box>

        {/* Tabs */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button
            variant={activeTab === 'participants' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setActiveTab('participants')}
            fullWidth
          >
            Participants ({participants.length})
          </Button>
          <Button
            variant={activeTab === 'activity' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setActiveTab('activity')}
            fullWidth
          >
            Activity
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Participants Tab */}
        {activeTab === 'participants' && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Active Participants
            </Typography>
            <List>
              {participants.map((participant) => (
                <ListItem key={participant.user_id}>
                  <ListItemAvatar>
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      variant="dot"
                      color={participant.active ? 'success' : 'default'}
                    >
                      <Avatar>
                        {participant.user_name.charAt(0).toUpperCase()}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={participant.user_name}
                    secondary={`Joined ${formatTimestamp(participant.joined_at)}`}
                  />
                  {participant.cursor_position && (
                    <Chip
                      label="Editing"
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </ListItem>
              ))}
            </List>

            {/* Invite Others */}
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Invite Collaborators
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Enter email or username"
                />
                <Button variant="contained" size="small">
                  Invite
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
            <List>
              {history.map((event, index) => (
                <ListItem key={event.id} sx={{ alignItems: 'flex-start' }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: `${getActionColor(event.action)}.main`, width: 32, height: 32 }}>
                      {getActionIcon(event.action)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={event.user_name}
                    secondary={
                      <>
                        <Typography variant="body2" color="text.secondary" component="span">
                          {getActionDescription(event.action, event.details)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="div">
                          {formatTimestamp(event.timestamp)}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Message Input */}
        <Box sx={{ mt: 'auto', pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <TextField
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            placeholder="Send a message to collaborators..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            size="small"
          />
          <Button
            fullWidth
            variant="contained"
            sx={{ mt: 1 }}
            disabled={!message.trim()}
          >
            Send Message
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

// Helper function to get action descriptions
const getActionDescription = (action: string, details: any): string => {
  switch (action) {
    case 'add_annotation':
      return `Added ${details?.annotation_type || 'an annotation'}`;
    case 'edit_annotation':
      return 'Edited an annotation';
    case 'reply_to_annotation':
      return details?.message_preview || 'Replied to an annotation';
    case 'resolve_annotation':
      return details?.resolution_note || 'Resolved an annotation';
    default:
      return 'Performed an action';
  }
};

export default CollaborativeView;